/**
 * The Wish tab: four sections, one (+).
 *
 * In order: My Chase Cards, My Chase Binders, Master Sets, Sub Sets. The last
 * two are the same row type with a different scope, because a sub set is a
 * filter over a master's slots rather than a set of its own - which is why Base
 * Set as a full master and Jungle as a 1st-Ed-only sub set sit side by side
 * naturally.
 *
 * Only the ranked ten is ever seen by profile matching. Chase binders and
 * master sets are invisible to it by design, so nothing here pings a stranger's
 * collection because somebody is working on a 216-slot set.
 *
 * Someone else's wish tab renders read only: their ten with its gaps showing,
 * their binders and sets, and nothing to hold.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronRight, Folder, Layers, Plus, Share2, Star } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useTheme } from '../../context/ThemeContext';
import haptics from '../../utils/haptics';
import { canWishListCard } from '../../utils/tcgPocket';
import { formatMoney } from '../../utils/catalogPrices';
import {
  TOP_TEN_LIMIT,
  topTenCounterLabel,
  topTenFullMessage,
  isTopTenFull,
} from '../../services/wishList';
import {
  fetchMasterSets,
  fetchWishBinders,
  fetchSetProgress,
  fetchSetSlots,
  removeWishSurface,
  createWishBinder,
  isDuplicateBinderNameError,
  duplicateBinderNameMessage,
} from '../../services/wishBinders';
import {
  shareWishSurface, shareTopTen, isMinorShareBlocked, MINOR_SHARE_MESSAGE,
} from '../../services/shareTargets';
import { fetchWishBinderProgress } from '../../services/wishOwned';
import * as cardData from '../../services/cardData';
// Imported directly rather than through the cardData barrel: this is the
// synchronous half of the same cache the barrel's getCardImages writes to, and
// the grid needs it before the first paint, not after a promise.
import { recallCardArt } from '../../services/cardData/artMemory';
import TopTenGrid from './TopTenGrid';
import WishSetRow from './WishSetRow';
import WishAddSheet, { WISH_ADD } from './WishAddSheet';
import WishBinderPicker from './WishBinderPicker';
import WishCardHoldSheet from './WishCardHoldSheet';
import WishSurfaceHoldSheet from './WishSurfaceHoldSheet';
import WishBinderDetail from './WishBinderDetail';
import SetSlotGrid from './SetSlotGrid';
import AddMasterSetSheet from './AddMasterSetSheet';
import AddBinderModal from '../modals/AddBinderModal';
import { holdProps } from './wishHold';
import { buildTopTenSlots } from './topTenSlots';

export default function WishTab({
  userId,
  items = [],
  loading = false,
  prices = {},
  pricingEnabled = false,
  sets = [],
  searchQuery = '',
  setTotals = null,
  // Hand the job to Explore rather than to a second picker of our own. The
  // owner, more than once: "REUSE EXPLORE with whatever explore filters and
  // sorts were on!"
  onGoPick = null,
  onSearchChange = null,
  isViewingOther = false,
  ownerName = '',
  onOpenCard,
  onRefresh,
  showToast,
}) {
  const { theme, isDark } = useTheme();
  const [binders, setBinders] = useState([]);
  // Each chase binder's size and how much of it is owned, keyed by binder id.
  // Its own read rather than a count, because Chase asked for a gauge on the
  // row and a gauge needs both halves of the fraction.
  const [binderProgress, setBinderProgress] = useState({});
  const [masters, setMasters] = useState([]);
  const [progress, setProgress] = useState({});
  const [openBinder, setOpenBinder] = useState(null);
  // Which chase binder the Explore picker is filling. Set alongside openBinder,
  // never instead of it, so closing the picker lands on the binder that just
  // gained cards rather than back at the top of the tab.
  // Whether the ranked ten is being filled through the same Explore picker the
  // chase binders use. It was its own search modal, and that was the owner's
  // loudest complaint: "it created its own search engine and all of that."

  const [addOpen, setAddOpen] = useState(false);
  const [pickBinderOpen, setPickBinderOpen] = useState(false);
  const [createBinderOpen, setCreateBinderOpen] = useState(false);
  const [masterSheetOpen, setMasterSheetOpen] = useState(false);
  const [heldCard, setHeldCard] = useState(null);
  const [heldSurface, setHeldSurface] = useState(null);
  // Which master set or sub set is open as a slot grid. One piece of state for
  // both sections, because they are one row type with a different scope.
  const [openSet, setOpenSet] = useState(null);
  const [sharingTen, setSharingTen] = useState(false);

  const topTen = useMemo(() => items.filter((i) => i.is_priority === true), [items]);
  const slots = useMemo(() => buildTopTenSlots(topTen, TOP_TEN_LIMIT), [topTen]);
  const filled = slots.filter((s) => s.card).length;
  const full = isTopTenFull(filled);

  const loadSurfaces = useCallback(async () => {
    if (!userId) return;
    try {
      const [wishBinders, masterRows, binderRows] = await Promise.all([
        fetchWishBinders(userId),
        fetchMasterSets(userId),
        // The acting profile is what ownership is counted for, and on somebody
        // else's chase list it is nobody: their binders show their size and no
        // bar, the same way their master sets do.
        fetchWishBinderProgress(userId, isViewingOther ? null : userId).catch(() => ({})),
      ]);
      setBinders(wishBinders);
      setMasters(masterRows);
      setBinderProgress(binderRows);
    } catch (e) {
      console.warn('[wish] could not read chase surfaces', e?.message);
    }
  }, [userId, isViewingOther]);

  useEffect(() => { loadSurfaces(); }, [loadSurfaces]);

  // The picker only needs how many cards each binder holds, which is one half of
  // the gauge's fraction, so it is derived rather than read a second time.
  const binderCounts = useMemo(() => {
    const out = {};
    for (const [id, p] of Object.entries(binderProgress)) out[id] = p.total;
    return out;
  }, [binderProgress]);

  // The ten's artwork, looked up by card id.
  //
  // `wish_list.image_url` is not a source of truth: the add path never wrote it,
  // so every row in the wild carries null and the tiles fell back to the
  // "card found, no image" panel. Even a populated column would be a URL frozen
  // at the moment somebody tapped Add, and card art in this app is self-hosted
  // and re-mirrored, so the id is the durable thing and the picture is looked up
  // the way every other grid looks it up.
  const [topTenArt, setTopTenArt] = useState({});
  // Whether the lookup is still out. A tile with no URL yet is not a card with
  // no picture, and the grid used to draw the second while the first was true.
  const [topTenArtPending, setTopTenArtPending] = useState(false);
  const topTenIds = useMemo(
    () => topTen.map((w) => w.tcgdex_card_id).filter(Boolean).join(','),
    [topTen],
  );
  useEffect(() => {
    if (!topTenIds) { setTopTenArt({}); setTopTenArtPending(false); return undefined; }
    const ids = topTenIds.split(',');

    // What this device already knows goes in before the first paint, so a
    // revisit is instant rather than a round trip's worth of empty squares.
    // Only a genuinely cold set of ids waits on the network.
    const { found, missing } = recallCardArt(ids);
    setTopTenArt(found);
    setTopTenArtPending(missing.length > 0);
    if (missing.length === 0) return undefined;

    let alive = true;
    cardData.getCardImages(ids)
      .then((all) => { if (alive) { setTopTenArt(all || {}); setTopTenArtPending(false); } })
      .catch((e) => {
        if (alive) setTopTenArtPending(false);
        console.warn('[wish] could not read the ten\'s artwork', e?.message);
      });
    return () => { alive = false; };
  }, [topTenIds]);

  // Progress is one call per row rather than one for the lot, because the count
  // is scoped and the scope lives on the row. They are fired together and
  // applied as they land, so a set with 408 slots does not hold up the others.
  useEffect(() => {
    if (masters.length === 0) return undefined;
    let alive = true;
    masters.forEach((row) => {
      // Progress is counted against whoever is asking, so on somebody else's
      // list it would be this person's own cards under their neighbour's set
      // name. Their row gets its size and no bar rather than a wrong one.
      // The acting profile is passed on purpose. A parent who has switched to a
      // child is looking at the CHILD's chase list, so the bar has to count the
      // child's cards; without this it counted the signed-in parent's and drew a
      // number that belongs to nobody on screen. The server allows only self or
      // a linked child, so there is nothing to gain by sending anything else.
      const read = isViewingOther
        ? fetchSetSlots(row.set_id, row.scope).then((slots) => ({ slots, filled: null }))
        : fetchSetProgress(row.set_id, row.scope, userId);
      read
        .then((p) => { if (alive) setProgress((prev) => ({ ...prev, [row.id]: p })); })
        .catch((e) => console.warn('[wish] progress failed for', row.name, e?.message));
    });
    return () => { alive = false; };
  }, [masters, isViewingOther, userId]);

  // What the ten would cost today. Pocket cards are left out of both halves,
  // since counting them in the denominator would report the list as part-priced
  // for ever.
  const value = useMemo(() => {
    const priceable = topTen.filter(canWishListCard);
    const priced = priceable.filter((w) => prices[w.tcgdex_card_id]);
    const total = priced.reduce((n, w) => n + (prices[w.tcgdex_card_id]?.price || 0), 0);
    return { total, priced: priced.length, priceable: priceable.length };
  }, [topTen, prices]);

  const masterSets = masters.filter((m) => !m.scope);
  const subSets = masters.filter((m) => !!m.scope);

  const deleteWishCard = async (card) => {
    const { error } = await supabase.from('wish_list').delete().eq('id', card.id);
    if (error) throw error;
    onRefresh?.();
  };

  // One handler for a chase binder and for a master set, because they are one
  // table with a different binder_type and the service branches on that. The
  // minor refusal comes from the database rather than from a flag read here, so
  // it cannot go stale against a profile that has not loaded yet.
  const shareSurface = async (surface) => {
    try {
      const res = await shareWishSurface(surface);
      if (res.method === 'clipboard') showToast?.('Link copied.', 'success');
    } catch (e) {
      if (isMinorShareBlocked(e)) { showToast?.(MINOR_SHARE_MESSAGE, 'warning'); return; }
      throw e;
    }
  };

  const shareTheTen = async () => {
    setSharingTen(true);
    try {
      const res = await shareTopTen(userId, ownerName);
      if (res.method === 'clipboard') showToast?.('Link copied.', 'success');
    } catch (e) {
      if (isMinorShareBlocked(e)) { showToast?.(MINOR_SHARE_MESSAGE, 'warning'); }
      else {
        console.warn('[wish] share the ten failed', e?.message);
        showToast?.('That share did not go through.', 'error');
      }
    } finally {
      setSharingTen(false);
    }
  };

  const removeSurface = async (surface) => {
    await removeWishSurface(surface.id);
    await loadSurfaces();
  };

  const handleAddPick = (choice) => {
    setAddOpen(false);
    if (choice === WISH_ADD.CARD) {
      // The cap is the database's word; this is the app saying the same thing
      // before the trigger has to.
      if (full) { haptics.warn(); showToast?.(topTenFullMessage(), 'warning'); return; }
      onGoPick?.('topTen');
      return;
    }
    if (choice === WISH_ADD.MASTER) { setMasterSheetOpen(true); return; }
    if (choice === WISH_ADD.TO_BINDER) { setPickBinderOpen(true); return; }
    if (choice === WISH_ADD.NEW_BINDER) { setCreateBinderOpen(true); }
  };

  const createBinder = async ({ name, image, isPrivate }) => {
    try {
      const row = await createWishBinder(userId, { name, image, isPrivate });
      await loadSurfaces();
      haptics.success();
      setOpenBinder(row);
    } catch (e) {
      if (isDuplicateBinderNameError(e)) {
        showToast?.(duplicateBinderNameMessage(name), 'warning');
      } else {
        console.warn('[wish] create binder failed', e?.message);
        showToast?.('Could not create that binder.', 'error');
      }
      throw e;
    }
  };

  if (loading) {
    return (
      <div style={styles.center}>
        <span style={{ ...styles.hint, color: theme.textSecondary }}>Loading your chase list...</span>
      </div>
    );
  }

  // Shopping for a master set is a level too, not a sheet: looking at a set has
  // to be free, and a bottom sheet with a search box in it was the thing that
  // made looking feel like committing.
  if (masterSheetOpen) {
    return (
      <AddMasterSetSheet
        sets={sets}
        setTotals={setTotals}
        query={searchQuery}
        onQueryChange={onSearchChange}
        userId={userId}
        theme={theme}
        pricingEnabled={pricingEnabled}
        onBack={() => setMasterSheetOpen(false)}
        onAdded={loadSurfaces}
        showToast={showToast}
      />
    );
  }

  // A set row opens into its slots, which is a level of its own rather than a
  // modal: the progress bar, the grid and its own sort all belong to the set,
  // and coming back lands on the list it was opened from.
  if (openSet) {
    return (
      <SetSlotGrid
        row={openSet}
        progress={progress[openSet.id]}
        asUserId={isViewingOther ? null : userId}
        theme={theme}
        pricingEnabled={pricingEnabled}
        readOnly={isViewingOther}
        onBack={() => setOpenSet(null)}
        onShare={shareSurface}
        onRemove={async (surface) => {
          await removeSurface(surface);
          // Nothing to come back to once it is not being chased.
          setOpenSet(null);
        }}
      />
    );
  }

  if (openBinder) {
    return (
      <WishBinderDetail
        binder={openBinder}
        theme={theme}
        isDark={isDark}
        /* The acting profile, so a parent switched into a child sees the CHILD's
           cards lit up rather than their own. Nobody's on somebody else's list. */
        asUserId={isViewingOther ? null : userId}
        pricingEnabled={pricingEnabled}
        readOnly={isViewingOther}
        onBack={() => setOpenBinder(null)}
        onOpenCard={onOpenCard}
        onAddCards={() => { haptics.tap(); onGoPick?.('binder', openBinder); }}
      />
    );
  }

  return (
    <div style={styles.wrap}>
      {!isViewingOther && (
        <button
          style={{ ...styles.addBar, backgroundColor: theme.warning }}
          onClick={() => { haptics.tap(); setAddOpen(true); }}
        >
          <Plus size={18} color="#000" />
          <span style={styles.addBarLabel}>Add to my chase list</span>
        </button>
      )}

      {/* 1. My Chase Cards - the ranked ten */}
      <section style={styles.section}>
        <div style={styles.sectionHead}>
          <span style={styles.sectionTitleRow}>
            <Star size={16} color={theme.warning} fill={theme.warning} />
            <span style={{ ...styles.sectionTitle, color: theme.text }}>
              {isViewingOther && ownerName ? `${ownerName}'s Chase Cards` : 'My Chase Cards'}
            </span>
          </span>
          <span style={styles.sectionTitleRow}>
            {/* The ten are not a row anybody can hold, so their share lives up
                here. It only appears once there is something to share: a link to
                an empty grid is a link to nothing. */}
            {!isViewingOther && filled > 0 && (
              <button
                style={{ ...styles.headerBtn, backgroundColor: theme.bgCard, borderColor: theme.border, color: theme.accent }}
                onClick={() => { haptics.tap(); shareTheTen(); }}
                disabled={sharingTen}
                aria-label="Share my chase cards"
              >
                <Share2 size={15} />
              </button>
            )}
            <span style={{ ...styles.sectionCount, color: theme.warning }}>
              {topTenCounterLabel(filled)}
            </span>
          </span>
        </div>
        <TopTenGrid
          items={topTen}
          theme={theme}
          art={topTenArt}
          artPending={topTenArtPending}
          pricingEnabled={pricingEnabled}
          prices={prices}
          readOnly={isViewingOther}
          onOpenCard={onOpenCard}
          onHoldCard={(card) => { haptics.heavy(); setHeldCard(card); }}
          onAdd={() => {
            haptics.tap();
            if (full) { haptics.warn(); showToast?.(topTenFullMessage(), 'warning'); return; }
            onGoPick?.('topTen');
          }}
        />
        {full && !isViewingOther && (
          <span style={{ ...styles.hint, color: theme.textMuted }}>{topTenFullMessage()}</span>
        )}
        {pricingEnabled && formatMoney(value.total) && (
          <span style={{ ...styles.hint, color: theme.textMuted }}>
            {formatMoney(value.total)} on the board · {value.priced} of {value.priceable} priced
          </span>
        )}
      </section>

      {/* 2. My Chase Binders */}
      <section style={styles.section}>
        <div style={styles.sectionHead}>
          <span style={styles.sectionTitleRow}>
            <Folder size={16} color={theme.accent} />
            <span style={{ ...styles.sectionTitle, color: theme.text }}>My Chase Binders</span>
          </span>
          <span style={{ ...styles.sectionCount, color: theme.textMuted }}>{binders.length}</span>
        </div>
        {binders.length === 0 ? (
          <span style={{ ...styles.hint, color: theme.textMuted }}>
            No chase binders yet. Unlimited binders, unlimited cards in each, and
            none of them touch matching.
          </span>
        ) : (
          binders.map((b) => (
            <BinderRow
              key={b.id}
              binder={b}
              progress={binderProgress[b.id]}
              theme={theme}
              readOnly={isViewingOther}
              onOpen={() => setOpenBinder(b)}
              onHold={() => { haptics.heavy(); setHeldSurface({ row: b, kind: 'chase binder' }); }}
            />
          ))
        )}
      </section>

      {/* 3. Master Sets, and 4. Sub Sets - one row type, two scopes */}
      <section style={styles.section}>
        <div style={styles.sectionHead}>
          <span style={styles.sectionTitleRow}>
            <Layers size={16} color={theme.accent} />
            <span style={{ ...styles.sectionTitle, color: theme.text }}>Master Sets</span>
          </span>
          <span style={{ ...styles.sectionCount, color: theme.textMuted }}>{masterSets.length}</span>
        </div>
        {masterSets.length === 0 ? (
          <span style={{ ...styles.hint, color: theme.textMuted }}>
            No master sets yet. A master set is the whole thing, printings
            included.
          </span>
        ) : (
          masterSets.map((row) => (
            <WishSetRow
              key={row.id}
              row={row}
              logo={logoFor(row.set_id, sets)}
              progress={progress[row.id]}
              theme={theme}
              readOnly={isViewingOther}
              onOpen={() => { haptics.tap(); setOpenSet(row); }}
              onHold={() => { haptics.heavy(); setHeldSurface({ row, kind: 'master set' }); }}
            />
          ))
        )}
      </section>

      <section style={styles.section}>
        <div style={styles.sectionHead}>
          <span style={styles.sectionTitleRow}>
            <Layers size={16} color={theme.purple} />
            <span style={{ ...styles.sectionTitle, color: theme.text }}>Sub Sets</span>
          </span>
          <span style={{ ...styles.sectionCount, color: theme.textMuted }}>{subSets.length}</span>
        </div>
        {subSets.length === 0 ? (
          <span style={{ ...styles.hint, color: theme.textMuted }}>
            No sub sets yet. A sub set is one printing or one card range inside a
            master set - Base Set Shadowless, or the Trainer Gallery.
          </span>
        ) : (
          subSets.map((row) => (
            <WishSetRow
              key={row.id}
              row={row}
              logo={logoFor(row.set_id, sets)}
              progress={progress[row.id]}
              theme={theme}
              readOnly={isViewingOther}
              onOpen={() => { haptics.tap(); setOpenSet(row); }}
              onHold={() => { haptics.heavy(); setHeldSurface({ row, kind: 'sub set' }); }}
            />
          ))
        )}
      </section>

      <WishAddSheet isOpen={addOpen} onClose={() => setAddOpen(false)} onPick={handleAddPick} />

      <WishBinderPicker
        isOpen={pickBinderOpen}
        binders={binders}
        counts={binderCounts}
        onClose={() => setPickBinderOpen(false)}
        onPick={(b) => {
          setPickBinderOpen(false);
          // Chase's second door. The binder is opened underneath so coming back
          // from Explore shows what was just filled.
          setOpenBinder(b);
          onGoPick?.('binder', b);
        }}
        onCreate={() => { setPickBinderOpen(false); setCreateBinderOpen(true); }}
      />

      <AddBinderModal
        isOpen={createBinderOpen}
        onClose={() => setCreateBinderOpen(false)}
        existingBinders={binders}
        onCreateBinder={createBinder}
      />

      <WishCardHoldSheet
        isOpen={!!heldCard}
        card={heldCard}
        position={heldCard?.position}
        onClose={() => setHeldCard(null)}
        onDelete={deleteWishCard}
      />

      <WishSurfaceHoldSheet
        isOpen={!!heldSurface}
        surface={heldSurface?.row}
        kindLabel={heldSurface?.kind}
        onClose={() => setHeldSurface(null)}
        onShare={shareSurface}
        onRemove={removeSurface}
      />
    </div>
  );
}

/** The catalogue's logo for a set, extensionless by convention. */
function logoFor(setId, sets) {
  return sets.find((s) => s.id === setId)?.logo || null;
}

/**
 * One chase binder row, with a gauge whose denominator is the binder's own size.
 *
 * Chase's words: "there's no gauge... If they only have ten cards in there or
 * fifty cards, it should be filling up to the amount of cards in that particular
 * custom binder." So a 50-card binder holding 12 owned cards reads 12 of 50, and
 * it is presented the way a master set row presents its slots - the same bar, the
 * same fraction, a different noun, because a binder holds cards and a master set
 * holds (card, printing) slots.
 *
 * `owned` of null is somebody else's binder, which gets its size and no bar for
 * the same reason a master set row does: counting it against the viewer's own
 * cards would put our Charizard under their name.
 */
function BinderRow({ binder, progress, theme, readOnly, onOpen, onHold }) {
  // A real ref, because the hold and the tap that iOS sends after it are two
  // events in two renders: a fresh object per render would forget the hold and
  // open the binder behind its own menu.
  const held = useRef(false);
  const total = Number(progress?.total) || 0;
  const known = progress ? progress.owned !== null && progress.owned !== undefined : false;
  const owned = known ? Number(progress.owned) || 0 : 0;
  const pct = known && total > 0 ? Math.min(100, Math.round((owned / total) * 100)) : 0;

  return (
    <div
      style={{ ...styles.binderRow, backgroundColor: theme.bgCard, borderColor: theme.border }}
      onClick={() => { if (!held.current) onOpen?.(); }}
      data-testid={`wish-binder-row-${binder.id}`}
      {...holdProps({ onHold, firedRef: held, disabled: readOnly })}
      onContextMenu={(e) => { e.preventDefault(); if (!readOnly) onHold?.(); }}
    >
      <Folder size={20} color={theme.accent} />
      <div style={styles.binderBody}>
        <span style={{ ...styles.binderName, color: theme.text }}>{binder.name}</span>
        {known && total > 0 && (
          <div style={{ ...styles.track, backgroundColor: theme.border }}>
            <div style={{ ...styles.fill, width: `${pct}%`, backgroundColor: theme.warning }} />
          </div>
        )}
        <span style={{ ...styles.binderCount, color: theme.textMuted }}>
          {known && total > 0 && `${owned} of ${total} cards \u00b7 ${pct}%`}
          {known && total === 0 && 'No cards yet'}
          {!known && `${total} ${total === 1 ? 'card' : 'cards'}`}
        </span>
      </div>
      <ChevronRight size={16} color={theme.textSecondary} />
    </div>
  );
}

const styles = {
  wrap: { display: 'flex', flexDirection: 'column', gap: '18px', padding: '12px 12px 32px' },
  center: { display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 20px' },
  // Full width and 50px tall, at the top of the content rather than up against
  // the status bar, where a tap lands on the system clock instead.
  addBar: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
    minHeight: '50px', borderRadius: '12px', border: 'none', cursor: 'pointer',
    outline: 'none', WebkitTapHighlightColor: 'transparent',
  },
  addBarLabel: { fontSize: '15px', fontWeight: '700', color: '#000' },
  section: { display: 'flex', flexDirection: 'column', gap: '8px' },
  sectionHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' },
  sectionTitleRow: { display: 'flex', alignItems: 'center', gap: '7px', minWidth: 0 },
  sectionTitle: { fontSize: '15px', fontWeight: '700' },
  sectionCount: { fontSize: '12px', fontWeight: '600' },
  headerBtn: {
    width: '28px', height: '28px', borderRadius: '8px', border: '1px solid', display: 'flex',
    alignItems: 'center', justifyContent: 'center', cursor: 'pointer', outline: 'none', flexShrink: 0,
    WebkitTapHighlightColor: 'transparent',
  },
  hint: { fontSize: '12px', lineHeight: 1.45 },
  binderRow: {
    // Taller than it was, because the row carries a bar and a fraction now, and
    // it is still one comfortable thumb target rather than two stacked ones.
    display: 'flex', alignItems: 'center', gap: '12px', minHeight: '64px', padding: '10px 12px',
    borderRadius: '12px', border: '1px solid', cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
  },
  binderBody: { display: 'flex', flexDirection: 'column', gap: '5px', flex: 1, minWidth: 0 },
  binderName: { fontSize: '15px', fontWeight: '600', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  // The same 6px track the master set row uses, so two rows in one list cannot
  // read as two different kinds of progress.
  track: { height: '6px', borderRadius: '3px', overflow: 'hidden' },
  fill: { height: '100%', borderRadius: '3px' },
  binderCount: { fontSize: '12px' },
};
